import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./msal-config";
import { useIsAuthenticated as useMsalIsAuthenticated } from "@azure/msal-react";
import { Navigate } from "react-router-dom";
import "./msalStyles.scss";
import CorticaLoader from "../../components/sharedComponents/CorticaLoader/CorticaLoader";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

const MsalLogin = () => {
  const { instance, inProgress } = useMsal();
  const msalIsAuthenticated = useMsalIsAuthenticated();
  const [loading, setLoading] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [error, setError] = useState(null);
  const activeMsAccount = instance.getActiveAccount();

  // Handle redirect callback on component mount
  useEffect(() => {
    const handleRedirectCallback = async () => {
      try {
        // Check if we're returning from a redirect
        const response = await instance.handleRedirectPromise();
        if (response) {
          console.log("[MSAL] Redirect callback successful:", response);
          // Set the active account
          instance.setActiveAccount(response.account);
          setRedirect(true);
        }
      } catch (error) {
        console.error("[MSAL] Redirect callback error:", error);
        setError(error.message || "Authentication failed after redirect.");
      }
    };

    handleRedirectCallback();
  }, []); // Run once on mount

  useEffect(() => {
    if (inProgress !== "none") {
      setLoading(true);
    } else {
      setLoading(false);
      const acquireToken = async () => {
        try {
          await instance.acquireTokenSilent(loginRequest);
          setRedirect(true);
        } catch (e) {
          if (e instanceof InteractionRequiredAuthError) {
            console.log('[Interaction Required with Microsoft]')
          } else {
            setError(e);
          }
        }
      };
      if (msalIsAuthenticated && activeMsAccount) {
        acquireToken();
      }
    }
  }, [inProgress, msalIsAuthenticated, activeMsAccount, instance]);

  if (redirect) {
    return <Navigate to="/providerappointments" />;
  }

  if (error) {
    console.error("Error acquiring token:", error);
  }

  const handleLogin = async () => {
    try {
      // Use redirect flow for all devices
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error("[MSAL Login] Login error:", error);
      setError(error.message || "Authentication failed. Please try again.");
    }
  };

  return (
    <>
      <div className="whole-container">
        {loading && <CorticaLoader message="Authentication in Progress" />}
        {error && (
          <div style={{ 
            color: '#d32f2f', 
            padding: '20px', 
            textAlign: 'center',
            backgroundColor: '#ffebee',
            borderRadius: '4px',
            margin: '20px'
          }}>
            <strong>Authentication Error:</strong><br />
            {error}
          </div>
        )}
        <div className="split-screen">
          <div className="leftSide">
            <div className="login-screen">
              <div className="logo">
                <svg
                  aria-label=""
                  fill="none"
                  viewBox="0 0 141 40"
                  className="g-logo"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.623 0a9.517 9.517 0 0 1-1.201 5.01 9.644 9.644 0 0 1-3.645 3.675 8.615 8.615 0 0 1-5.69 1.24 13.11 13.11 0 0 1 2.62 3.4 13.264 13.264 0 0 0 8.296-4.495A13.006 13.006 0 0 0 20.145 0h-3.522Z"
                    fill="#10389A"
                  ></path>
                  <path
                    d="M16.623 0a9.517 9.517 0 0 1-1.201 5.01 9.644 9.644 0 0 1-3.645 3.675 8.615 8.615 0 0 1-5.69 1.24 13.11 13.11 0 0 1 2.62 3.4 13.264 13.264 0 0 0 8.296-4.495A13.006 13.006 0 0 0 20.145 0h-3.522ZM35.02 8.48a9.782 9.782 0 0 1-10.029.205 8.514 8.514 0 0 1-3.943-4.25 12.956 12.956 0 0 1-1.661 3.946c2 2.547 4.882 4.273 8.09 4.845a13.334 13.334 0 0 0 9.291-1.743L35.019 8.48ZM36.787 28.486a9.661 9.661 0 0 1-3.77-3.54 9.529 9.529 0 0 1-1.383-4.958 8.408 8.408 0 0 1 1.768-5.497c-1.39.41-2.838.59-4.287.534a12.957 12.957 0 0 0-.237 9.357 13.121 13.121 0 0 0 6.141 7.12l1.768-3.016ZM20.164 40a9.517 9.517 0 0 1 1.195-5.008 9.644 9.644 0 0 1 3.638-3.677 8.603 8.603 0 0 1 5.691-1.24 13.111 13.111 0 0 1-2.62-3.406 13.276 13.276 0 0 0-8.292 4.502A13.019 13.019 0 0 0 16.636 40h3.528ZM1.78 31.526a9.782 9.782 0 0 1 10.029-.199 8.458 8.458 0 0 1 3.942 4.25 12.96 12.96 0 0 1 1.661-3.945 13.23 13.23 0 0 0-8.105-4.86A13.32 13.32 0 0 0 0 28.536c.338.534 1.442 2.413 1.78 2.99ZM0 11.52a9.66 9.66 0 0 1 3.762 3.543 9.528 9.528 0 0 1 1.378 4.956 8.36 8.36 0 0 1-1.774 5.496c1.392-.41 2.842-.59 4.293-.534a12.97 12.97 0 0 0 .238-9.353 13.134 13.134 0 0 0-6.13-7.123L0 11.52ZM18.419 23.2c1.775 0 3.214-1.422 3.214-3.175s-1.44-3.174-3.214-3.174c-1.775 0-3.214 1.42-3.214 3.174 0 1.753 1.439 3.174 3.214 3.174ZM55.194 31.542c5.335 0 8.943-3.44 9.445-7.84h-3.671c-.408 2.665-2.73 4.71-5.774 4.71-4.141 0-7.154-2.975-7.154-7.964 0-4.99 2.981-7.964 7.154-7.964 3.044 0 5.24 1.859 5.68 4.493h3.67c-.438-4.431-4.078-7.623-9.35-7.623-6.244 0-10.856 4.152-10.856 11.094 0 6.973 4.675 11.094 10.856 11.094ZM73.831 31.542c4.738 0 8.159-3.347 8.159-8.088 0-4.71-3.42-8.026-8.159-8.026-4.738 0-8.158 3.316-8.158 8.026s3.42 8.088 8.158 8.088Zm-4.675-8.088c0-3.037 1.945-5.113 4.675-5.113 2.762 0 4.676 2.107 4.676 5.113 0 3.068-1.914 5.175-4.676 5.175-2.73 0-4.675-2.107-4.675-5.175ZM83.338 31.294h3.42v-6.043c0-4.493 2.008-6.662 4.456-6.662.627 0 1.38.093 2.102.402v-3.253c-.502-.217-1.13-.31-1.788-.31-1.977 0-3.86 1.053-4.77 2.634v-2.386h-3.42v15.618ZM102 31.542c.753 0 1.537-.124 2.196-.403v-2.882a5.657 5.657 0 0 1-1.757.31c-1.349 0-2.228-.837-2.228-2.51v-7.592h3.86v-2.79h-3.86v-3.873H96.76v3.874h-2.573v2.789h2.573v7.87c0 3.379 2.227 5.207 5.24 5.207ZM105.723 31.294h3.42V15.676h-3.42v15.618Zm-.502-20.298c0 1.209.941 2.107 2.196 2.107 1.255 0 2.197-.898 2.197-2.107 0-1.208-.942-2.107-2.197-2.107-1.255 0-2.196.899-2.196 2.107ZM118.79 31.542c4.079 0 6.966-2.603 7.436-5.857h-3.451c-.314 1.767-1.852 2.944-3.985 2.944-2.542 0-4.487-2.138-4.487-5.175 0-3.006 1.945-5.113 4.487-5.113 2.008 0 3.514 1.115 3.859 2.85h3.452c-.471-3.47-3.169-5.763-7.311-5.763-4.644 0-7.97 3.377-7.97 8.026 0 4.68 3.357 8.088 7.97 8.088ZM132.811 31.542c2.133 0 4.079-1.147 4.738-2.51v2.262H141v-9.823c0-3.843-2.824-6.043-6.777-6.043-3.891 0-6.401 2.479-6.841 5.547h3.452c.251-1.829 1.506-2.82 3.389-2.82 2.008 0 3.326 1.084 3.326 2.913v.403l-3.201.31c-4.895.526-7.06 2.138-7.06 5.237 0 2.696 2.165 4.524 5.523 4.524Zm-2.103-4.68c0-1.549 1.13-2.2 3.797-2.51l3.044-.371v.434c0 2.479-1.663 4.431-4.173 4.431-1.632 0-2.668-.744-2.668-1.983Z"
                    fill="#10389A"
                  ></path>
                </svg>
              </div>
              <div className="login-button">
                <h3 className="welcome-heading">Welcome Back!</h3>
                <div className="welcome-subheading-login-password avg-margin-tp">
                  Just tap here to log in with your Microsoft credentials as a
                  Provider
                </div>
                <button
                  onClick={handleLogin}
                  className="btn btn-block avg-margin-tp"
                >
                  Log in with Microsoft
                </button>
              </div>
            </div>
          </div>
          <div className="rightSide">
            <div className="right-wrapper">
              <div className="right-side-img">
                <img
                  src="https://images.ctfassets.net/67wivm6jm6qo/1QKtnV4uBmgey7NJqPdv97/d38f6a64a99ea849acd4882d787bc99a/Image.png"
                  alt="man interacting with child at table"
                />
              </div>
              <div className="right-side-txt carousel-container">
                <div className="right-side-txt carousel-container">
                  <p className="carousel-text">
                    We offer a wide range of medical and therapy services,
                    including ABA, diagnostic testing, speech-language therapy,
                    occupational therapy, counseling, and much more. Visit our
                    <a
                      href="https://www.corticacare.com/services"
                      className="link-text"
                    >
                      Services
                    </a>
                    page to learn more. Not all services may be available in all
                    locations.
                  </p>

                  <p className="carousel-text">
                    We serve children with any neurodevelopmental difference. We
                    can schedule your telemedicine diagnosis right away, click
                    <a
                      href="https://www.corticacare.com/welcomevisit"
                      className="link-text"
                    >
                      Make An Appointment
                    </a>
                    or call
                    <a className="link-text" href="tel:888-885-5068">
                      888-885-5068
                    </a>
                    to enroll.
                  </p>

                  <p className="carousel-text">
                    We specialize in autism care, but we also serve a broader
                    range of neurodiverse patients, including those with
                    developmental delay, ADHD, epilepsy, genetic syndromes,
                    sensory processing disorder, and more. Our team has
                    expertise in diagnosing and treating a wide spectrum of
                    developmental challenges.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MsalLogin;